/**
 * Modale plein écran : l'élève envoie 1 à 3 photos d'un exercice réel + une
 * note optionnelle à son prof particulier actif. Capture via l'appareil photo
 * natif du téléphone (`<input capture="environment">`, caméra arrière) —
 * pas de flux getUserMedia live : pour scanner un document, l'appli caméra
 * native gère mieux la mise au point/luminosité qu'un aperçu custom, et cette
 * approche fonctionne partout (web + WebView Capacitor) sans plugin natif.
 *
 * Rendue via un PORTAIL (`createPortal` → `document.body`) : ouverte depuis
 * `TutorCard`, profondément imbriquée dans `<main>` (StudentLayout), alors que
 * `StudentBottomNav` (fond translucide + `backdrop-blur-xl`, `z-10`) est un
 * FRÈRE de `<main>`, pas un enfant — un `fixed inset-0` imbriqué restait
 * derrière elle. `z-[70]` (au-delà de la nav) une fois portée à `document.body`.
 *
 * ⚠️ VRAIE cause de la transparence (le portail seul ne suffisait pas) :
 * sous `.theme-ocean` (posé sur `<html>`, `globals.css`), `--color-white`
 * vaut `rgba(255,255,255,0.085)` — `bg-white` devient volontairement du
 * « verre » partout dans l'app (cf. cartes `jr-card`), sauf sous un ancêtre
 * qui porte `text-white` (le « neutraliseur » qui repose `--color-white:#fff`
 * pour ce sous-arbre). Cette modale plein écran doit au contraire être
 * OPAQUE — donc `bg-[#ffffff]` (couleur littérale, indépendante du thème),
 * jamais `bg-white` ici. Même idiome que les boutons flottants sur photo
 * (`ExercisePlayerV2`, `ChallengeCard`) ou les switches (`_app.profile.tsx`).
 *
 * ⚠️ Corollaire texte : `.theme-ocean` INVERSE aussi toute l'échelle `ink-*`
 * (ink-900 ≈ blanc, ink-500/600 ≈ gris-bleu clair — pensée pour du texte sur
 * fond sombre). Sur cette carte "papier" réellement blanche, `text-ink-900`
 * devient quasi invisible. Donc ICI, texte/bordures en hex littéral
 * (valeurs du preset LIGHT non inversé), jamais `text-ink-*`/`border-ink-*`.
 */

import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { X, Camera, Loader2, Send, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import { SUBJECTS } from '@jeraisonne/db';
import type { SubjectSlug } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { useStudentProfile } from '@/hooks/use-student-profile';
import { useSubmitExercisePhotos, MAX_SUBMISSION_PHOTOS } from '@/lib/exercise-submissions/api';
import { resolveSubmissionSubjects } from '@/lib/tutor-subjects';

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';

// Selects littéraux (pas `ink-*`/`bg-white`) : cf. le commentaire d'en-tête
// sur l'inversion de thème `.theme-ocean`.
const selectCls =
  'w-full rounded-xl border border-[#e6e4de] bg-[#ffffff] px-3 py-2 text-sm text-[#18160f] focus:outline-none focus:ring-2 focus:ring-primary-400';

export interface SubmitExerciseModalProps {
  studentId: string;
  tutorId: string;
  tutorName: string;
  /** Matière(s) couvertes par ce tuteur (`tutor_links.subjects`) — vide = toutes matières. */
  tutorSubjects: SubjectSlug[];
  onClose: () => void;
}

export function SubmitExerciseModal({
  studentId,
  tutorId,
  tutorName,
  tutorSubjects,
  onClose,
}: SubmitExerciseModalProps) {
  const [photos, setPhotos] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [subject, setSubject] = useState<SubjectSlug | ''>('');
  const [chapterId, setChapterId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const submit = useSubmitExercisePhotos();
  const studentProfile = useStudentProfile(studentId);
  const level = studentProfile.data?.level ?? null;

  // Borné aux matières de l'élève ∩ celles de CE tuteur (migration 0227) —
  // évite d'envoyer par erreur une photo de maths au prof d'anglais.
  const availableSubjects = useMemo(() => {
    const levelSubjects = level ? SUBJECTS.filter((s) => s.levels.includes(level)) : SUBJECTS;
    const allowed = resolveSubmissionSubjects(
      studentProfile.data?.subjects ?? [],
      tutorSubjects,
      levelSubjects.map((s) => s.value),
    );
    return levelSubjects.filter((s) => allowed.includes(s.value));
  }, [level, studentProfile.data?.subjects, tutorSubjects]);

  const chapters = useQuery({
    queryKey: ['submission-chapters', subject, level],
    enabled: !!subject && !!level,
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from('chapters')
        .select('id, title, display_order')
        .eq('subject', subject as SubjectSlug)
        .eq('level', level!)
        .eq('status', 'published')
        .order('display_order');
      if (err) throw err;
      return data ?? [];
    },
    staleTime: 5 * 60_000,
  });

  function handleSubjectChange(value: string) {
    setSubject(value as SubjectSlug | '');
    setChapterId('');
  }

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    setError(null);
    setPhotos((prev) => [...prev, ...files].slice(0, MAX_SUBMISSION_PHOTOS));
  }

  function removePhoto(index: number) {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    setError(null);
    try {
      await submit.mutateAsync({ studentId, tutorId, note, chapterId: chapterId || null, photos });
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (done) {
    return createPortal(
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className="w-full max-w-xs rounded-3xl bg-[#ffffff] p-6 text-center shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-4xl" aria-hidden="true">
            📤
          </p>
          <p className="font-display mt-2 text-lg font-bold text-[#18160f]">
            Envoyé à {tutorName} !
          </p>
          <p className="mt-1 text-sm text-[#78746a]">Il/elle va recevoir une notification.</p>
          <Button onClick={onClose} fullWidth className="mt-4">
            Fermer
          </Button>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-[#ffffff]"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between border-b border-[#f4f3f0] px-4 py-3 pt-[max(env(safe-area-inset-top),0.75rem)]">
        <h2 className="font-display text-base font-bold text-[#18160f]">Envoyer un exercice</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center text-[#78746a]"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <p className="text-sm text-[#78746a]">
          Prends en photo ton exercice (1 à {MAX_SUBMISSION_PHOTOS} photos) pour {tutorName}.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#5c5850]">
              Matière (optionnel)
            </span>
            <select
              value={subject}
              onChange={(e) => handleSubjectChange(e.target.value)}
              className={selectCls}
            >
              <option value="">—</option>
              {availableSubjects.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#5c5850]">
              Chapitre
            </span>
            <select
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              disabled={!subject || chapters.isLoading}
              className={`${selectCls} disabled:opacity-50`}
            >
              <option value="">—</option>
              {(chapters.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#5c5850]">
            Un mot pour ton prof (optionnel)
          </span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="Je bloque sur la question 2…"
            className="w-full rounded-xl border border-[#e6e4de] bg-[#ffffff] px-3 py-2 text-sm text-[#18160f] placeholder:text-[#a09b90]"
          />
        </label>

        <div className="grid grid-cols-3 gap-2">
          {photos.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="aspect-3/4 relative overflow-hidden rounded-xl border border-[#e6e4de]"
            >
              <img
                src={URL.createObjectURL(file)}
                alt={`Photo ${i + 1}`}
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                aria-label={`Retirer la photo ${i + 1}`}
                className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white active:scale-[0.95]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {photos.length < MAX_SUBMISSION_PHOTOS && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="aspect-3/4 flex min-h-[2.75rem] flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-[#cbc8be] text-[#a09b90] active:scale-[0.97]"
            >
              <Camera className="h-6 w-6" />
              <span className="text-[11px] font-semibold">Ajouter</span>
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          capture="environment"
          aria-label="Choisir une photo"
          className="hidden"
          onChange={handleFiles}
        />

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b91c1c]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="border-t border-[#f4f3f0] p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <Button onClick={handleSubmit} disabled={photos.length === 0 || submit.isPending} fullWidth>
          {submit.isPending ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Envoi…
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Send className="h-4 w-4" /> Envoyer à {tutorName}
            </span>
          )}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
